import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AdminPasswordProtected = ({ children }) => {
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordError, setAdminPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const { user, isAdmin, isRecruiter } = useAuth();

  useEffect(() => {
    // Check if admin password is already verified in session storage
    const adminVerified = sessionStorage.getItem('adminPasswordVerified');
    if (adminVerified === 'true') {
      setIsAuthenticated(true);
    } else {
      // Only show password dialog if user is admin/recruiter and not already authenticated
      if (isAdmin || isRecruiter) {
        setShowPasswordDialog(true);
      } else {
        // If user is not admin/recruiter, redirect to home
        navigate('/');
      }
    }
  }, [isAdmin, isRecruiter, navigate]);

  const handleAdminPasswordSubmit = () => {
    if (adminPassword === 'RISO@2025') {
      setShowPasswordDialog(false);
      setAdminPassword('');
      setAdminPasswordError('');
      setIsAuthenticated(true);
      sessionStorage.setItem('adminPasswordVerified', 'true');
    } else {
      setAdminPasswordError('Incorrect admin password');
    }
  };

  const handleAdminPasswordCancel = () => {
    setShowPasswordDialog(false);
    setAdminPassword('');
    setAdminPasswordError('');
    // Redirect to home page if admin password is cancelled
    navigate('/');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAdminPasswordSubmit();
    }
  };

  if (!isAuthenticated) {
    return (
      <Dialog 
        open={showPasswordDialog} 
        onClose={handleAdminPasswordCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Lock sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Admin Portal Access Required
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" color="textSecondary">
              This area requires administrator access. Please enter the admin password to continue.
            </Typography>
          </Box>
          {adminPasswordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {adminPasswordError}
            </Alert>
          )}
          <TextField
            margin="normal"
            required
            fullWidth
            label="Admin Password"
            type={showPassword ? 'text' : 'password'}
            id="admin-password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            error={!!adminPasswordError}
            helperText={adminPasswordError}
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle admin password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAdminPasswordCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleAdminPasswordSubmit} 
            variant="contained" 
            color="primary"
            disabled={!adminPassword.trim()}
          >
            Access Admin Portal
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return children;
};

export default AdminPasswordProtected;
